import { apiAction, API_ERROR, API_SUCCESS } from "../dux/api";
import { apiCallComplete, apiCallStart } from "../dux/apiStatus";
import {
  FETCH_ACTIVITY_LOGS_REQUEST,
  FETCH_ACTIVITY_SUMMARY_REQUEST,
  fetchActivityLogsSuccess,
  fetchActivityLogsFailure,
  fetchActivitySummarySuccess,
  fetchActivitySummaryFailure,
} from "../dux/activityLog";
import {
  getActivityLogsUrl,
  getActivitySummaryUrl,
} from "../helpers/apiEndPoints";
import dayjs from "dayjs";

const activityLogMiddleware = () => (next) => (action) => {
  next(action);

  // Fetch activity logs
  if (action.type === FETCH_ACTIVITY_LOGS_REQUEST) {
    const { filters, pagination, location, sport } = action.payload;

    // Validate dates before making the API call
    if (!filters.start_date || !filters.end_date) {
      console.error("Missing start_date or end_date in filters:", filters);
      return;
    }

    // Convert ISO strings to dayjs objects for validation and formatting
    let startDate, endDate;
    try {
      startDate = dayjs(filters.start_date);
      endDate = dayjs(filters.end_date);
    } catch (error) {
      console.error("Error parsing dates:", error);
      return;
    }

    // Check if dates are valid dayjs objects
    if (!startDate.isValid() || !endDate.isValid()) {
      console.error("Invalid dates in filters:", {
        start_date: filters.start_date,
        end_date: filters.end_date,
        start_valid: startDate.isValid(),
        end_valid: endDate.isValid(),
      });
      return;
    }

    next(apiCallStart({ api: "fetchActivityLogs" }));

    // Build query parameters
    const params = new URLSearchParams({
      page: (pagination.page + 1).toString(),
      limit: pagination.rowsPerPage.toString(),
      order_by: "created_at",
      order_direction: "DESC",
    });

    if (location) params.append("location", location);
    if (sport) params.append("sport", sport);
    if (filters.activity_type)
      params.append("activity_type", filters.activity_type);
    if (filters.module) params.append("module", filters.module);
    if (filters.is_successful !== "")
      params.append("is_successful", filters.is_successful);
    if (startDate) {
      const formattedStartDate = startDate.format("YYYY-MM-DD");
      const isoStartDate = startDate.toISOString();

      // Try using ISO string first, fallback to date format
      params.append("start_date", isoStartDate);
    }
    if (endDate) {
      const formattedEndDate = endDate.format("YYYY-MM-DD");
      const isoEndDate = endDate.toISOString();

      // Try using ISO string first, fallback to date format
      params.append("end_date", isoEndDate);
    }
    if (filters.user_id) params.append("user_id", filters.user_id);
    if (filters.related_entity_type)
      params.append("related_entity_type", filters.related_entity_type);
    if (filters.related_entity_id)
      params.append("related_entity_id", filters.related_entity_id);

    const url = `${getActivityLogsUrl()}?${params}`;

    next(
      apiAction({
        url,
        feature: FETCH_ACTIVITY_LOGS_REQUEST,
        method: "GET",
        meta: { filters, pagination, location, sport },
      })
    );
  }

  // Fetch activity summary
  if (action.type === FETCH_ACTIVITY_SUMMARY_REQUEST) {
    const { location, sport } = action.payload;

    next(apiCallStart({ api: "fetchActivitySummary" }));

    const params = new URLSearchParams({
      days: "30",
    });

    if (location) params.append("location", location);
    if (sport) params.append("sport", sport);

    next(
      apiAction({
        url: `${getActivitySummaryUrl()}?${params}`,
        feature: FETCH_ACTIVITY_SUMMARY_REQUEST,
        method: "GET",
        meta: { location, sport },
      })
    );
  }

  // Handle API success for activity logs
  if (action.type === `${FETCH_ACTIVITY_LOGS_REQUEST} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "fetchActivityLogs" }));
    // Dispatch the success action that the reducer expects
    next(fetchActivityLogsSuccess(action.payload));
  }

  // Handle API error for activity logs
  if (action.type === `${FETCH_ACTIVITY_LOGS_REQUEST} ${API_ERROR}`) {
    next(apiCallComplete({ api: "fetchActivityLogs" }));
    // Dispatch the failure action that the reducer expects
    next(
      fetchActivityLogsFailure(
        action.payload?.message || "Failed to fetch activity logs"
      )
    );
  }

  // Handle API success for activity summary
  if (action.type === `${FETCH_ACTIVITY_SUMMARY_REQUEST} ${API_SUCCESS}`) {
    next(apiCallComplete({ api: "fetchActivitySummary" }));
    // Dispatch the success action that the reducer expects
    next(fetchActivitySummarySuccess(action.payload));
  }

  // Handle API error for activity summary
  if (action.type === `${FETCH_ACTIVITY_SUMMARY_REQUEST} ${API_ERROR}`) {
    next(apiCallComplete({ api: "fetchActivitySummary" }));
    // Dispatch the failure action that the reducer expects
    next(
      fetchActivitySummaryFailure(
        action.payload?.message || "Failed to fetch activity summary"
      )
    );
  }
};

export default activityLogMiddleware;
